import _ from "lodash";

export const formatAmount = (amount) => {
  if (amount == null) return ""; // Handle null or undefined
  const num = parseFloat(amount);
  return isNaN(num) ? "" : num.toString();
};

export const DOC_WIDGETS = [
  "text",
  "number",
  "boolean",
  "select",
  "radio",
  "date",
];

const WIDGET_TO_SCHEMA = {
  text: { type: "string" },
  number: { type: "number" },
  boolean: { type: "boolean" },
  date: { type: "string", format: "date" },
  select: { type: "string", enum: [], widget: "select" },
  radio: { type: "string", enum: [], widget: "radio" },
};

export const getDocFieldWidget = (col) => {
  if (!col) return "text";
  if (col.type === "boolean") return "boolean";
  if (col.type === "number") return "number";
  if (col.format === "date") return "date";
  if (col.widget === "radio") return "radio";
  if (col.widget === "select") return "select";
  // Legacy data: pre-widget-marker select fields had only `enum` set.
  if (Array.isArray(col.enum) && col.enum.length > 0) return "select";
  return "text";
};

export const applyDocWidget = (col, widget) => {
  const next = { ...col };
  // strip widget-specific keys, then re-apply
  delete next.format;
  delete next.widget;
  delete next.enum;
  const schema = WIDGET_TO_SCHEMA[widget] || WIDGET_TO_SCHEMA.text;
  next.type = schema.type;
  if (schema.format !== undefined) next.format = schema.format;
  if (schema.widget !== undefined) next.widget = schema.widget;
  if (schema.enum !== undefined) {
    // preserve previous enum if it was already set, otherwise start empty
    next.enum = Array.isArray(col.enum) ? col.enum : [];
  }
  return next;
};

// Select widgets render the first <option> when value is "", so the UI shows
// a choice the form state doesn't actually hold — saving without touching
// the dropdown would persist "". Default to the first enum entry so what's
// displayed matches what gets saved.
export const defaultDocLeafValue = (leaf) => {
  const widget = getDocFieldWidget(leaf);
  if (widget === "boolean") return false;
  if (widget === "select" && Array.isArray(leaf.enum) && leaf.enum.length > 0) {
    return leaf.enum[0];
  }
  return "";
};

export const EMPTY_DOC_SCHEMA = {
  row_label: "",
  columns: [],
  document_columns: [],
};
export const EMPTY_DOC_VALUES = { rows: [], document_values: {} };

export const genRowId = () =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `r_${Math.random().toString(36).slice(2)}_${Date.now().toString(36)}`;

export const buildTextColumn = (title, index) => ({
  kind: "field",
  id: `f_${index + 1}`,
  title: title || `Column ${index + 1}`,
  type: "string",
});

const normalizeDocTitle = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");

// Auto-assign import columns to existing leaf fields by (normalized) title.
// Each field is claimed at most once; unmatched columns map to "" (skip).
export const matchColumnsToLeaves = (headers, leaves) => {
  const leafIdByTitle = new Map();
  (leaves || []).forEach((leaf) => {
    const key = normalizeDocTitle(leaf.title);
    if (key && !leafIdByTitle.has(key)) leafIdByTitle.set(key, leaf.id);
  });
  const mapping = {};
  const used = new Set();
  (headers || []).forEach((header, i) => {
    const leafId = leafIdByTitle.get(normalizeDocTitle(header));
    mapping[i] = leafId && !used.has(leafId) ? leafId : "";
    if (mapping[i]) used.add(leafId);
  });
  return mapping;
};

const TRUTHY_CELL_VALUES = new Set(["x", "true", "1", "yes", "y", "ja", "j", "wahr"]);
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const GERMAN_DATE_RE = /^(\d{1,2})\.(\d{1,2})\.(\d{4})$/;

const parseNumericCell = (text) => {
  let normalized = text.replace(/\s/g, "");
  const lastComma = normalized.lastIndexOf(",");
  const lastDot = normalized.lastIndexOf(".");
  if (lastComma !== -1 && lastDot !== -1) {
    // Both separators present: the later one is the decimal mark, the other
    // a thousands separator ("1.234,56" vs "1,234.56").
    normalized =
      lastComma > lastDot
        ? normalized.replace(/\./g, "").replace(",", ".")
        : normalized.replace(/,/g, "");
  } else if (lastComma !== -1) {
    normalized = normalized.replace(",", ".");
  }
  const num = Number(normalized);
  return Number.isFinite(num) && normalized !== "" ? num : null;
};

// Coerce a raw imported cell (always text) into the value shape the target
// field's widget expects. Unparseable values are kept verbatim instead of
// being dropped — losing data silently is worse than an odd-looking cell.
export const coerceDocValue = (leaf, raw) => {
  const widget = getDocFieldWidget(leaf);
  const text = String(raw ?? "").trim();
  if (widget === "boolean") return TRUTHY_CELL_VALUES.has(text.toLowerCase());
  if (text === "") return "";
  if (widget === "number") {
    const num = parseNumericCell(text);
    return num !== null ? num : text;
  }
  if (widget === "date") {
    if (ISO_DATE_RE.test(text)) return text;
    const m = text.match(GERMAN_DATE_RE);
    if (m) return `${m[3]}-${m[2].padStart(2, "0")}-${m[1].padStart(2, "0")}`;
    return text;
  }
  if (widget === "select" || widget === "radio") {
    const match = (leaf.enum || []).find(
      (choice) => normalizeDocTitle(choice) === text.toLowerCase(),
    );
    return match !== undefined ? match : text;
  }
  return String(raw ?? "");
};

const isEnumWidget = (leaf) =>
  ["select", "radio"].includes(getDocFieldWidget(leaf));

// A cell introduces a new option when its coerced value is non-empty and not
// one of the field's configured choices. Coercion canonicalizes matching
// values to the exact enum entry, so a strict includes() check is enough.
export const isNewEnumValue = (leaf, raw) => {
  if (!isEnumWidget(leaf)) return false;
  const value = coerceDocValue(leaf, raw);
  return value !== "" && !(leaf.enum || []).includes(value);
};

// Collect imported values per mapped select/radio field that don't match any
// configured option — deduped case-insensitively, in file order. Later case
// variants canonicalize to the first occurrence via the working option list.
export const collectNewEnumValues = (rows, mapping, leaves) => {
  const leafById = new Map((leaves || []).map((l) => [l.id, l]));
  const additions = {};
  Object.entries(mapping || {}).forEach(([colIdx, leafId]) => {
    if (!leafId) return;
    const leaf = leafById.get(leafId);
    if (!leaf || !isEnumWidget(leaf)) return;
    const working = [...(leaf.enum || [])];
    const added = [];
    (rows || []).forEach((row) => {
      const raw = row[Number(colIdx)] ?? "";
      const value = coerceDocValue({ ...leaf, enum: working }, raw);
      if (value === "" || working.includes(value)) return;
      working.push(value);
      added.push(value);
    });
    if (added.length) additions[leafId] = added;
  });
  return additions;
};

// Append collected new options to their fields, recursing into groups.
// Fields without additions are returned untouched.
export const extendDocColumnEnums = (columns, additionsById) =>
  (columns || []).map((col) => {
    if (col?.kind === "group") {
      return { ...col, columns: extendDocColumnEnums(col.columns, additionsById) };
    }
    const additions = additionsById?.[col?.id];
    if (!additions?.length) return col;
    return { ...col, enum: [...(col.enum || []), ...additions] };
  });

// Flat list of mappable leaf fields with a display label that includes the
// group path ("Group › Field") so same-named fields stay distinguishable.
export const docLeafOptions = (columns, prefix = "") => {
  const out = [];
  for (const col of columns || []) {
    if (col?.kind === "group") {
      const groupPrefix = prefix ? `${prefix} › ${col.title}` : col.title;
      out.push(...docLeafOptions(col.columns, groupPrefix));
    } else if (col?.kind === "field") {
      const title = col.title || col.id;
      out.push({
        id: col.id,
        leaf: col,
        label: prefix ? `${prefix} › ${title}` : title,
      });
    }
  }
  return out;
};

const stripRowMeta = ({ _id, ...rest }) => rest;

export const buildDocumentationPayload = (values, template) => {
  const templateRow = template?.documentation?.rows?.[0] || {};
  return {
    rows: (values?.documentation?.rows || [])
      .map(stripRowMeta)
      .filter((row) =>
        Object.keys(row).some((key) => !_.isEqual(row[key], templateRow[key])),
      ),
    document_values: _.isObject(values?.documentation?.document_values)
      ? values.documentation.document_values
      : {},
  };
};

export const docLeaves = (columns) => {
  const out = [];
  for (const col of columns || []) {
    if (col?.kind === "group") out.push(...docLeaves(col.columns));
    else if (col?.kind === "field") out.push(col);
  }
  return out;
};

export const docHasGroups = (columns) =>
  (columns || []).some((c) => c?.kind === "group");

export const reconcileDocumentation = (source, schema) => {
  const leafIds = docLeaves(schema?.columns || []).map((c) => c.id);
  const docIds = (schema?.document_columns || [])
    .filter((c) => c?.kind === "field")
    .map((c) => c.id);

  const rows = (source?.rows || []).map((row) =>
    leafIds.reduce((acc, id) => {
      if (Object.prototype.hasOwnProperty.call(row, id)) acc[id] = row[id];
      return acc;
    }, {}),
  );

  const srcValues = source?.document_values || {};
  const document_values = docIds.reduce((acc, id) => {
    if (Object.prototype.hasOwnProperty.call(srcValues, id)) {
      acc[id] = srcValues[id];
    }
    return acc;
  }, {});

  return { rows, document_values };
};

export const seedDocumentation = (template, deliveryNote, { isAddMode }) => {
  const isCopy = isAddMode && Boolean(deliveryNote);
  const sourceWasImported = Boolean(
    deliveryNote?.documentation_schema?.imported_from,
  );

  let documentation_schema;
  let documentation;
  if (isCopy && sourceWasImported) {
    // The source note's documentation came from an Excel/CSV import — its
    // schema is detached from the template. Carry it over verbatim so the
    // copy keeps the same columns and values instead of being reconciled
    // back to the template schema (which would drop every imported column).
    documentation_schema = _.cloneDeep(deliveryNote.documentation_schema);
    documentation = _.cloneDeep(
      deliveryNote.documentation || { rows: [], document_values: {} },
    );
  } else if (isCopy) {
    documentation_schema = _.cloneDeep(
      template?.documentation_schema || EMPTY_DOC_SCHEMA,
    );
    documentation = reconcileDocumentation(
      deliveryNote.documentation,
      documentation_schema,
    );
  } else {
    documentation_schema =
      deliveryNote?.documentation_schema?.columns?.length > 0
        ? _.cloneDeep(deliveryNote.documentation_schema)
        : _.cloneDeep(template?.documentation_schema || EMPTY_DOC_SCHEMA);

    documentation = deliveryNote?.documentation || { rows: [] };
    if (
      (documentation.rows || []).length === 0 &&
      (template?.documentation?.rows || []).length > 0
    ) {
      documentation = _.cloneDeep(template.documentation);
    }
  }

  documentation = {
    ...documentation,
    rows: (documentation.rows || []).map((r) => ({ ...r, _id: genRowId() })),
  };

  return { documentation_schema, documentation };
};

const maxIdNum = (columns, prefix) => {
  const re = new RegExp(`^${prefix}_(\\d+)$`);
  let max = -1;
  for (const col of columns || []) {
    if (col?.kind === "field") {
      const m = String(col.id || "").match(re);
      if (m) max = Math.max(max, parseInt(m[1], 10));
    } else if (col?.kind === "group") {
      max = Math.max(max, maxIdNum(col.columns, prefix));
    }
  }
  return max;
};

export const nextColId = (columns) => `col_${maxIdNum(columns, "col") + 1}`;
export const nextDocId = (columns) => `doc_${maxIdNum(columns, "doc") + 1}`;

export const makeField = (id, title = "", widget = "text") =>
  applyDocWidget(
    {
      kind: "field",
      id,
      title,
      "x-unit": null,
      description: "",
    },
    widget,
  );

export const makeDocField = (id, widget = "boolean") =>
  applyDocWidget(
    {
      kind: "field",
      id,
      title: "",
      "x-unit": null,
      description: "",
    },
    widget,
  );

export const makeGroup = (title = "") => ({
  kind: "group",
  title,
  columns: [],
});

export const canMoveDocColumnUp = (columns, path) => {
  if (path.groupIdx !== undefined) return true;
  return path.idx > 0;
};

export const canMoveDocColumnDown = (columns, path) => {
  if (path.groupIdx !== undefined) return true;
  return path.idx < (columns?.length || 0) - 1;
};

export const moveDocColumn = (columns, path, direction) => {
  const cols = columns || [];
  if (direction !== -1 && direction !== 1) return cols;

  if (path.groupIdx === undefined) {
    const { idx } = path;
    const targetIdx = idx + direction;
    if (targetIdx < 0 || targetIdx >= cols.length) return cols;
    const neighbor = cols[targetIdx];
    if (neighbor?.kind === "group") {
      const field = cols[idx];
      const nextNeighborCols =
        direction === -1
          ? [...neighbor.columns, field]
          : [field, ...neighbor.columns];
      const next = [...cols];
      next[targetIdx] = { ...neighbor, columns: nextNeighborCols };
      next.splice(idx, 1);
      return next;
    }
    const next = [...cols];
    [next[idx], next[targetIdx]] = [next[targetIdx], next[idx]];
    return next;
  }

  const { groupIdx, idx } = path;
  const group = cols[groupIdx];
  if (!group || group.kind !== "group") return cols;
  const groupCols = group.columns || [];
  const targetIdx = idx + direction;

  if (targetIdx >= 0 && targetIdx < groupCols.length) {
    const nextGroupCols = [...groupCols];
    [nextGroupCols[idx], nextGroupCols[targetIdx]] = [
      nextGroupCols[targetIdx],
      nextGroupCols[idx],
    ];
    const next = [...cols];
    next[groupIdx] = { ...group, columns: nextGroupCols };
    return next;
  }

  const field = groupCols[idx];
  const remainingGroupCols = groupCols.filter((_, i) => i !== idx);
  const next = [...cols];
  next[groupIdx] = { ...group, columns: remainingGroupCols };
  const insertIdx = direction === -1 ? groupIdx : groupIdx + 1;
  next.splice(insertIdx, 0, field);
  return next;
};

export const formatDocCellForDisplay = (col, value) => {
  if (value === null || value === undefined || value === "") return "";
  const widget = getDocFieldWidget(col);
  if (widget === "boolean") return value ? "X" : "";
  if (widget === "number" && col["x-unit"]) return `${value} ${col["x-unit"]}`;
  return String(value);
};
